import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import { Check, RefreshCw, X } from 'lucide-react';
import { PageScaffold } from '../components/page/PageScaffold';
import { EmptyState } from '../components/EmptyState';
import { useFamily, useInvites, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { ApiError } from '../lib/api';
import { appLocale } from '../lib/format';
import { useT, type MessageKey } from '../i18n';

const ROLE_LABEL: Record<string, MessageKey> = {
  root: 'account.role.root',
  admin: 'account.role.admin',
  member: 'account.role.member',
};

export function InvitesPage() {
  const { invites, loading, refresh, accept, decline } = useInvites();
  // The mark while the invitations load (M66, D248).
  const invitesState = useLoadingState({
    loading,
    hasData: invites.length > 0,
    retry: () => void refresh(),
  });
  const { loadFamilies } = useFamily();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAccept = async (inviteId: string) => {
    try {
      const result = await accept(inviteId);
      await loadFamilies();
      toast({
        title: t('account.invites.joined'),
        description: t('account.invites.joinedHint'),
        status: 'success',
      });
      if (result.familyId) navigate('/family');
    } catch (err) {
      toast({
        title: t('account.invites.acceptFailed'),
        description:
          err instanceof ApiError
            ? err.message
            : t('account.profile.unknownError'),
        status: 'error',
      });
    }
  };

  const handleDecline = async (inviteId: string) => {
    try {
      await decline(inviteId);
      toast({ title: t('account.invites.declined'), status: 'info' });
    } catch (err) {
      toast({
        title: t('account.invites.declineFailed'),
        description:
          err instanceof ApiError
            ? err.message
            : t('account.profile.unknownError'),
        status: 'error',
      });
    }
  };

  return (
    <PageScaffold
      title={t('account.menu.invitations')}
      subtitle={t('account.invites.subtitle')}
      actions={
        <IconButton
          aria-label={t('account.invites.checkAgain')}
          icon={<RefreshCw size={20} />}
          variant="ghost"
          colorScheme="gray"
          onClick={() => refresh()}
        />
      }
    >
      {invitesState.show ? (
        <LoadingPanel state={invitesState} />
      ) : invites.length === 0 ? (
        <EmptyState
          kind="chats"
          title={t('account.invites.emptyTitle')}
          body={t('account.invites.emptyBody')}
        />
      ) : (
        <VStack align="stretch" spacing="10px">
          {invites.map((invite) => (
            <Box
              key={invite.id}
              bg="surface"
              borderWidth="1px"
              borderColor="line"
              borderRadius="card"
              p={4}
            >
              <HStack spacing={3} align="center" mb={4}>
                <Avatar
                  name={invite.family.name}
                  src={invite.family.avatarUrl ?? undefined}
                  borderRadius="16px"
                />
                <Box minW={0}>
                  <Text fontWeight={700} fontSize="17px" noOfLines={1}>
                    {invite.family.name}
                  </Text>
                  {invite.family.handle && (
                    <Text fontSize="14px" color="muted" noOfLines={1}>
                      @{invite.family.handle}
                    </Text>
                  )}
                  {/* Who asked, by name (D351). */}
                  <Text fontSize="15px" color="ink.body" mt={1}>
                    {t('account.invites.invitedYou', {
                      name: invite.invitedByUser.displayName,
                      family: invite.family.name,
                    })}
                  </Text>
                  <Text fontSize="14px" color="muted">
                    {t('account.invites.details', {
                      role: ROLE_LABEL[invite.familyRole]
                        ? t(ROLE_LABEL[invite.familyRole]!).toLowerCase()
                        : invite.familyRole,
                      handle: invite.invitedByUser.handle,
                      date: new Date(invite.expiresAt).toLocaleDateString(
                        appLocale(),
                        {
                          day: 'numeric',
                          month: 'short',
                        }
                      ),
                    })}
                  </Text>
                </Box>
              </HStack>
              <HStack spacing={2}>
                <Button
                  flex="1"
                  h="48px"
                  leftIcon={<Check size={18} />}
                  onClick={() => handleAccept(invite.id)}
                >
                  {t('account.invites.join')}
                </Button>
                <Button
                  flex="1"
                  h="48px"
                  variant="outline"
                  colorScheme="gray"
                  borderColor="line"
                  leftIcon={<X size={18} />}
                  onClick={() => handleDecline(invite.id)}
                >
                  {t('account.invites.noThanks')}
                </Button>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
    </PageScaffold>
  );
}
